import { METRIC_COLOURS } from '@/constants'

export type MetricKey =
  | 'demuxInRate'
  | 'demuxToDecodeLagMs'
  | 'renderRatio'
  | 'downloadKbps'
  | 'e2eLatency'

export type AxisId = 'rate' | 'ms' | 'ratio' | 'kbps'

export type MetricDef = {
  key: MetricKey
  label: string
  color: string
  axis: AxisId
  /** Optional dash style for the line (e.g. lag drawn dashed). */
  dash?: string
}

export const METRICS: ReadonlyArray<MetricDef> = [
  { key: 'demuxInRate',        label: 'Demux in (/s)',      color: METRIC_COLOURS.mos, axis: 'rate' },
  { key: 'demuxToDecodeLagMs', label: 'Demux→Dec lag (ms)', color: METRIC_COLOURS.std, axis: 'ms', dash: '4 2' },
  { key: 'renderRatio',        label: 'Render in/out',      color: METRIC_COLOURS.bdr, axis: 'ratio' },
  { key: 'downloadKbps',       label: 'Download (kbps)',    color: METRIC_COLOURS.g2g, axis: 'kbps' },
  { key: 'e2eLatency',         label: 'E2E latency (ms)',   color: METRIC_COLOURS.rbf, axis: 'ms' },
]

export const ALL_METRIC_KEYS: ReadonlyArray<MetricKey> =
  METRICS.map((m) => m.key)

/** Extra series per primary metric (data keys on `ChartPoint` / `SeriesRow`). */
export type MetricLineDef = { dataKey: string; label: string; color: string; dash?: string }

export const METRIC_LINE_SERIES: Record<MetricKey, MetricLineDef[]> = {
  demuxInRate: [
    { dataKey: 'demuxInRate', label: 'Display', color: METRIC_COLOURS.mos },
    { dataKey: 'demuxInRate_mean', label: 'Mean', color: METRIC_COLOURS.mos, dash: '4 3' },
    { dataKey: 'demuxInRate_min', label: 'Min', color: METRIC_COLOURS.mos, dash: '2 3' },
    { dataKey: 'demuxInRate_max', label: 'Max', color: METRIC_COLOURS.mos, dash: '1 3' },
  ],
  demuxToDecodeLagMs: [
    { dataKey: 'demuxToDecodeLagMs', label: 'Display', color: METRIC_COLOURS.std },
    { dataKey: 'demuxToDecodeLagMs_mean', label: 'Mean', color: METRIC_COLOURS.std, dash: '4 3' },
    { dataKey: 'demuxToDecodeLagMs_p95', label: 'p95', color: METRIC_COLOURS.std, dash: '2 3' },
  ],
  renderRatio: [
    { dataKey: 'renderRatio', label: 'Display', color: METRIC_COLOURS.bdr },
  ],
  downloadKbps: [
    { dataKey: 'downloadKbps', label: 'Display', color: METRIC_COLOURS.g2g },
    { dataKey: 'downloadKbps_mean', label: 'Mean', color: METRIC_COLOURS.g2g, dash: '4 3' },
    { dataKey: 'downloadKbps_p50', label: 'p50', color: METRIC_COLOURS.g2g, dash: '2 3' },
  ],
  e2eLatency: [
    { dataKey: 'e2eLatency', label: 'Display', color: METRIC_COLOURS.rbf },
    { dataKey: 'e2eLatency_mean', label: 'Mean', color: METRIC_COLOURS.rbf, dash: '4 3' },
    { dataKey: 'e2eLatency_p95', label: 'p95', color: METRIC_COLOURS.rbf, dash: '2 3' },
  ],
}

export type SeriesRow = {
  tsMs: number
} & Partial<Record<string, number | null>>

export type ChartPoint = {
  ts: number
} & Partial<Record<string, number | null>>

export type Selection = {
  dimension: string
  value: string
  /** Scope for `view=stream` timeseries (defaults to global listing). */
  sliceType?: string
  sliceValue?: string
}

export type TimeWindow = '1h' | '12h' | '24h' | 'all' | 'custom'
export type ApiTimeWindow = Exclude<TimeWindow, 'custom'>
